import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { AppConfig } from './app.config';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {

  private config: AppConfig;
  private backendBaseURL: string;

  constructor(private http: HttpClient) {}

  get apiURL(): string { return this.backendBaseURL; }

  async loadConfig(): Promise<void> {
    const bootstrap = await this.http
      .get<any>('/assets/config.json')
      .pipe(take(1))
      .toPromise();
    const env = environment.envName;
    this.backendBaseURL = bootstrap[env]?.apiBaseURL;

    // Step 2: Now use the correct backend URL to fetch secrets from Key Vault
    const configUrl = `${this.backendBaseURL}Configuration/frontend-config`;
    this.config = await this.http
      .get<AppConfig>(configUrl)
      .pipe(take(1))
      .toPromise();

    this.config.apiURL = this.resolveApiUrl(this.config.apiURL);
    this.publishToExtensionStorage();
  }

  /**
   * The API returns a host-relative apiURL ("/api/") because the web app is served from the same
   * host as the backend — correct there, and left alone. The Chrome extension runs the same code
   * on chrome-extension://<id>/, where that path resolves inside the extension package instead:
   * POST token becomes chrome-extension://<id>/api/token and dies as net::ERR_FILE_NOT_FOUND. Only
   * that case is rewritten, against the backend named in the bootstrap config.
   *
   * A backend that returns no apiURL at all (a local dev instance with no Key Vault) falls back to
   * the bootstrap backend in both apps, which is the only host it could have meant.
   */
  private resolveApiUrl(apiURL: string): string {
    const base = (this.backendBaseURL || '').replace(/\/+$/, '');
    if (!apiURL) { return `${base}/api/`; }
    if (apiURL.startsWith('http://') || apiURL.startsWith('https://')) { return apiURL; }

    const isExtension = typeof location !== 'undefined' && location.protocol === 'chrome-extension:';
    if (!isExtension) { return apiURL; }

    return base + (apiURL.startsWith('/') ? apiURL : `/${apiURL}`);
  }

  /** Extension only: the service worker's own fetches read apiUrl from chrome.storage, seeded from
   *  environment.ts at bundle-load time. Republish the resolved values so it and the app agree on
   *  one backend. No-op in the web app, where chrome.storage doesn't exist. */
  private publishToExtensionStorage(): void {
    const c: any = (typeof window !== 'undefined') ? (window as any).chrome : null;
    if (!c || !c.storage || !c.storage.local) { return; }
    c.storage.local.set({ apiUrl: this.config.apiURL, frontUrl: this.config.frontURL });
  }

  get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    if (!this.config) {
      throw new Error(`AppConfig not loaded yet. Key requested: ${String(key)}`);
    }
    return this.config[key];
  }

  // ─── Convenience Getters ──────────────────────────────────────────────────
  get frontURL(): string { return this.config.frontURL; }
  get intercomAppId(): string { return this.config.intercomAppId; }
  /** "synced" only when the backend switched the website's chat on and named its script; else "intercom". */
  get supportChat(): 'synced' | 'intercom' {
    return this.config?.supportChat === 'synced' && !!this.config?.supportChatScriptUrl ? 'synced' : 'intercom';
  }
  get supportChatScriptUrl(): string | undefined { return this.config?.supportChatScriptUrl; }
  get posthogKey(): string { return this.config.posthogKey; }
  get posthogHost(): string { return this.config.posthogHost; }
  get turnstileSiteKey(): string { return this.config.turnstileSiteKey; }
  get emailDownloadLink(): string { return this.config.emailDownloadLink; }

  /** SD-1207: null on an older backend, which the bank-feed UI treats as "not available here". */
  get stripePublishableKey(): string {
    return this.config ? this.config.stripePublishableKey : null;
  }

  /** SD-1218: whether the client should resolve the sidemenu from server entitlements.
   *  Tolerates a missing config (an older backend) and any casing of "true". */
  get moduleEntitlementsEnabled(): boolean {
    const raw = this.config ? this.config.moduleEntitlementsEnabled : null;
    return String(raw).toLowerCase() === 'true';
  }

  // ─── Xero OAuth Links ─────────────────────────────────────────────────────
  private xeroOAuthBase(callbackUrl: string, state = '123'): string {
    return `https://login.xero.com/identity/connect/authorize?response_type=code` +
      `&client_id=${this.config.xeroClientId}` +
      `&redirect_uri=${callbackUrl}` +
      `&scope=openid profile offline_access accounting.transactions accounting.settings` +
      ` accounting.contacts accounting.attachments accounting.reports.read` +
      ` accounting.reports.bankstatement.read email&state=${state}`;
  }

  get xeroOauth2Link(): string {
    return this.xeroOAuthBase(this.config.xeroCallbackUrl);
  }

  get xeroOAuth2SignUpLink(): string {
    return this.xeroOAuthBase(this.config.xeroSignUpCallbackUrl, 'afiGl5r9s2w');
  }

  get xeroOAuth2SignInLink(): string {
    return `https://login.xero.com/identity/connect/authorize?response_type=code` +
      `&client_id=${this.config.xeroClientId}` +
      `&redirect_uri=${this.config.xeroSignInCallbackUrl}` +
      `&scope=openid profile email&state=afiGl5r9s2w`;
  }
}